import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, setDoc, updateDoc, where } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../firebase-config';
import QldStageRenderer from './QldStageRenderer';
import { getQldMobile, isValidQldMobile, normalizeQldMobile } from '../qldContact';
import ModalComponent from './Modal';
import Input from './Input';
import Dropdown from './Dropdown';
import Logo from '../assets/Logo.svg';
import qldAssessmentQuestions from '../data/qldAssessmentQuestions';
import qldAssessment2Questions from '../data/qldAssessment2Questions';
import qldAssessment3Questions from '../data/qldAssessment3Questions';
import qldAssessment4Questions from '../data/qldAssessment4Questions';
import {
    buildQldFullName,
    buildQldLinkPayload,
    buildStageUnlockPatch,
    isQldAssessmentStage,
    QLD_STAGE_KEYS,
    QLD_STAGE_LABELS,
    toTitleCase,
} from '../qldWorkflow';
import { generateGcitReportTemplate } from '../utils/gcitReportTemplate';
import { clearPageMessage, showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';

const generateQldAssessmentPdfUrl = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/generateQldAssessmentPdf';
const generateQldCardsUrl = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/generateQldCards';
const formatQldCourseDate = (link) => {
    const rawDate = link?.createdAt?.toDate?.() || link?.createdAt || link?.createdDateLabel;
    const date = rawDate ? new Date(rawDate) : null;

    if (!date || Number.isNaN(date.getTime())) {
        return link?.createdDateLabel || 'Not specified';
    }

    return new Intl.DateTimeFormat('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);
};
const assessmentQuestionMap = {
    assessment1: qldAssessmentQuestions,
    assessment2: qldAssessment2Questions,
    assessment3: qldAssessment3Questions,
    assessment4: qldAssessment4Questions,
};

const hasManualAnswerSection = (question) => (
    Boolean(question?.textAnswer)
    || Boolean(question?.partBQuestion)
    || ['form', 'tableForm', 'imageTableForm', 'textTableForm'].includes(question?.type)
);
const granularImageTableQuestionIds = new Set([7, 51, 52]);
const isGranularImageTableQuestion = (question) => (
    question?.type === 'imageTableForm' && granularImageTableQuestionIds.has(question?.id)
);
const isGranularTableFormQuestion = (question) => (
    question?.type === 'tableForm' && question?.tableForm?.studentHeader?.[0] === 'Hazard Report Form'
);
const getEmbeddedAssessorMarkKeys = (question) => {
    if (question?.type === 'tableForm') {
        return (question.tableForm?.rows || []).reduce((acc, row) => {
            if (row?.assessorField?.inputType === 'select' && row.assessorField.options?.includes('C') && row.assessorField.options?.includes('NYC')) {
                acc.push(row.assessorField.key);
            } else if (row?.assessorOnly && row?.inputType === 'select' && row.options?.includes('C') && row.options?.includes('NYC')) {
                acc.push(row.key);
            }

            return acc;
        }, []);
    }

    if (question?.type === 'imageTableForm' && question?.assessorMarkPerRow) {
        return (question.table?.body || []).map((_, rowIndex) => `assessorMark_${rowIndex}`);
    }

    return [];
};
const getFormRowReviewKey = (rowIndex) => `row_${rowIndex}`;
const getTableRowReviewKey = (rowIndex) => `tableRow_${rowIndex}`;
const getImageTableCellReviewKey = (rowIndex, columnKey) => `tableCell_${rowIndex}_${columnKey}`;
const getTableFormFieldReviewKey = (rowIndex, fieldKey) => `tableField_${rowIndex}_${fieldKey}`;
const getImageTableRowReviewKeys = (question, rowIndex) => (
    isGranularImageTableQuestion(question)
        ? (question.inputColumns || []).map((column) => getImageTableCellReviewKey(rowIndex, column.key))
        : [getTableRowReviewKey(rowIndex)]
);
const getInlineRowReviewKeys = (question) => {
    if (question?.type === 'form') {
        return (question.options || []).reduce((acc, row, rowIndex) => {
            if (row?.type === 'section') {
                return acc;
            }

            acc.push(getFormRowReviewKey(rowIndex));
            return acc;
        }, []);
    }

    if (question?.type === 'textTableForm') {
        return (question.table?.body || []).map((_, rowIndex) => getTableRowReviewKey(rowIndex));
    }

    if (question?.type === 'imageTableForm' && question?.assessorMarkPerRow) {
        const tableRowKeys = (question.table?.body || []).flatMap((_, rowIndex) => getImageTableRowReviewKeys(question, rowIndex));
        const partBRowKeys = (question.partBFields || []).map((_, rowIndex) => `partBRow_${rowIndex}`);
        return [...tableRowKeys, ...partBRowKeys];
    }

    if (question?.type === 'tableForm') {
        return (question.tableForm?.rows || []).reduce((acc, row, rowIndex) => {
            if (row?.assessorOnly || row?.type === 'section' || row?.type === 'static') {
                return acc;
            }

            if (isGranularTableFormQuestion(question)) {
                if (row?.type === 'inlineFields') {
                    acc.push(...(row.fields || []).map((field) => getTableFormFieldReviewKey(rowIndex, field.key)));
                } else if (row?.key) {
                    acc.push(getTableFormFieldReviewKey(rowIndex, row.key));
                }
                return acc;
            }

            acc.push(getTableRowReviewKey(rowIndex));
            return acc;
        }, []);
    }

    return [];
};
const hasEmbeddedAssessorControls = (question) => getEmbeddedAssessorMarkKeys(question).length > 0;
const areEqual = (left, right) => JSON.stringify(left || null) === JSON.stringify(right || null);

const getStageCellStyles = (stage) => {
    const interactive = stage?.completed || stage?.unlocked || Boolean(stage?.reviewStatus);
    return { cursor: interactive ? 'pointer' : 'default' };
};

const getStageBadgeStyles = (stage) => {
    const status = stage?.reviewStatus || '';

    if (status === 'passed') {
        return {
            background: '#e8f5e9',
            border: '1px solid #b9ddbd',
            color: '#286134',
        };
    }

    if (status === 'submitted_for_review' || status === 'student_submitted') {
        return {
            background: '#fff8c5',
            border: '1px solid #eadb72',
            color: '#6b5b00',
        };
    }

    if (status === 'needs_resubmission') {
        return {
            background: '#fff1dd',
            border: '1px solid #e8c99f',
            color: '#7a4b14',
        };
    }

    if (status === 'not_satisfactory' || status === 'cannot_verify') {
        return {
            background: '#fdecec',
            border: '1px solid #e7bcbc',
            color: '#8a3030',
        };
    }

    if (stage?.unlocked) {
        return {
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            color: '#4b5563',
        };
    }

    return {
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        color: '#9ca3af',
    };
};

const getStageStatusLabel = (stage) => {
    if (stage?.reviewStatus === 'passed') {
        return 'Completed';
    }

    if (stage?.reviewStatus === 'submitted_for_review' || stage?.reviewStatus === 'student_submitted') {
        return 'Submitted';
    }

    if (stage?.reviewStatus === 'needs_resubmission') {
        return 'Reattempting';
    }

    if (stage?.reviewStatus === 'not_satisfactory') {
        return 'Not Satisfactory';
    }

    if (stage?.reviewStatus === 'cannot_verify') {
        return 'Not Verified';
    }

    if (stage?.unlocked) {
        return 'Released';
    }

    return 'Not Released';
};

const qldTableShellStyles = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '14px',
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.11), 0 2px 6px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
    marginBottom: '20px',
};

const qldTableHeaderCellStyles = {
    padding: '9px 8px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    background: '#fffbea',
    borderBottom: '1px solid #eee19a',
    whiteSpace: 'nowrap',
};

const qldStageBadgeBaseStyles = {
    width: '82px',
    minHeight: '30px',
    padding: '5px 8px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    lineHeight: 1.2,
    textAlign: 'center',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
};

const getAssessmentManualQuestionIndexes = (stageKey) => (
    (assessmentQuestionMap[stageKey] || []).reduce((acc, question, index) => {
        if (hasManualAnswerSection(question)) {
            acc.push(index);
        }

        return acc;
    }, [])
);
const getAssessmentReviewQuestionIndexes = (student, stageKey) => {
    const defaultManualQuestionIndexes = getAssessmentManualQuestionIndexes(stageKey);
    const retryQuestionIndexes = student?.responses?.[stageKey]?.manualRetryQuestionIndexes || [];
    const hasSavedReview = Object.keys(student?.trainerResponses?.[stageKey]?.questionMarks || {}).length > 0;
    const isResubmittedForReview = student?.stages?.[stageKey]?.reviewStatus === 'submitted_for_review';

    if (isResubmittedForReview && hasSavedReview && retryQuestionIndexes.length > 0) {
        return retryQuestionIndexes;
    }

    return defaultManualQuestionIndexes;
};
const getQuestionReviewState = (question, questionIndex, responseData, reviewData) => {
    const embeddedMarkKeys = getEmbeddedAssessorMarkKeys(question);
    const questionResponse = responseData?.responses?.[questionIndex] || {};
    const inlineRowReviewKeys = getInlineRowReviewKeys(question);

    const questionMark = reviewData?.questionMarks?.[questionIndex] || {};
    if (inlineRowReviewKeys.length > 0) {
        const rowMarks = inlineRowReviewKeys.map((key) => questionMark?.rowMarks?.[key]?.mark || '');
        const hasAnyRowMark = rowMarks.some(Boolean);

        if (hasAnyRowMark) {
            return {
                mark: rowMarks.some((value) => value === 'NYC') ? 'NYC' : 'C',
                missing: rowMarks.length === 0 || rowMarks.some((value) => !value),
                comment: '',
            };
        }
    }

    if (embeddedMarkKeys.length > 0) {
        const marks = embeddedMarkKeys.map((key) => questionResponse?.[key] || '');
        return {
            mark: marks.some((value) => value === 'NYC') ? 'NYC' : 'C',
            missing: marks.some((value) => !value),
            comment: questionResponse?.assessorComments || '',
        };
    }

    if (inlineRowReviewKeys.length > 0) {
        return {
            mark: 'C',
            missing: true,
            comment: '',
        };
    }

    if (question?.assessorMarkPerRow) {
        const rowMarks = Object.values(questionMark.rowMarks || {});
        return {
            mark: rowMarks.some((row) => row?.mark === 'NYC') ? 'NYC' : 'C',
            missing: rowMarks.length === 0 || rowMarks.some((row) => !row?.mark),
            comment: '',
        };
    }

    return {
        mark: questionMark.mark || '',
        missing: !questionMark.mark,
        comment: questionMark.mark === 'NYC' ? (questionMark.comment || '') : '',
    };
};
const canGenerateStudentPdf = (student) => (
    Boolean(student?.stages) && QLD_STAGE_KEYS.every((stageKey) => student.stages?.[stageKey]?.completed)
);
const hasQldCardSignature = (student) => Boolean(student?.responses?.declaration?.studentSignature);
const canGenerateQldCardForStudent = (student) => canGenerateStudentPdf(student);
const buildQldAuditEvent = (eventType, detail = {}) => ({
    eventType,
    ...detail,
    createdAt: new Date().toISOString(),
});
const lockStagesFrom = (stages = {}, stageKey) => {
    const startIndex = QLD_STAGE_KEYS.indexOf(stageKey);

    if (startIndex === -1) {
        return stages;
    }

    return QLD_STAGE_KEYS.slice(startIndex).reduce((acc, key) => ({
        ...acc,
        [key]: {
            ...(stages[key] || {}),
            unlocked: false,
            completed: false,
            completedAt: '',
        }
    }), { ...stages });
};
const copyCourseLink = async (url) => {
    await navigator.clipboard.writeText(url);
};
const copyStudentLink = async (student) => {
    if (!student?.id || !student?.linkId) {
        return;
    }

    const url = `${window.location.origin}/qldEoi?linkId=${student.linkId}&sid=${student.id}`;
    await navigator.clipboard.writeText(url);
};

const DisplayQldAdmin = ({ userData, trainerFilter, onTrainerFilterChange, trainerDirectory = [], createSignal, onCreateHandled, onCreateStudentLink, studentNumbersSignal, gcitSignal, onToolbarStateChange, initialSelectedLinkId = '', onSelectedLinkChange }) => {
    const [links, setLinks] = useState([]);
    const [studentCountsByLink, setStudentCountsByLink] = useState({});
    const [selectedLinkId, setSelectedLinkId] = useState(initialSelectedLinkId);
    useEffect(() => {
        onSelectedLinkChange?.(selectedLinkId);
    }, [selectedLinkId, onSelectedLinkChange]);
    const [students, setStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [selectedStageKey, setSelectedStageKey] = useState('lln');
    const [generatingPdfStudentId, setGeneratingPdfStudentId] = useState('');
    const [bulkReleasingStageKey, setBulkReleasingStageKey] = useState('');
    const [isGeneratingGcitReport, setIsGeneratingGcitReport] = useState(false);
    const [showGenerateCardNumbersModal, setShowGenerateCardNumbersModal] = useState(false);
    const [showGenerateCardsModal, setShowGenerateCardsModal] = useState(false);
    const [showStageReviewModal, setShowStageReviewModal] = useState(false);
    const [minCardNo, setMinCardNo] = useState('');
    const [maxCardNo, setMaxCardNo] = useState('');
    const [availableQldCardNumbers, setAvailableQldCardNumbers] = useState([]);
    const [isCreatingQldCardNumbers, setIsCreatingQldCardNumbers] = useState(false);
    const [isLoadingQldCardNumbers, setIsLoadingQldCardNumbers] = useState(false);
    const [isGeneratingQldCards, setIsGeneratingQldCards] = useState(false);
    const [qldCenterMessage, setQldCenterMessage] = useState(null);
    const qldCenterMessageTimer = useRef(null);
    const lastStudentNumbersSignal = useRef(0);
    const lastGcitSignal = useRef(0);

    const showQldCenterMessage = useCallback((text, type = 'success') => {
        if (qldCenterMessageTimer.current) {
            window.clearTimeout(qldCenterMessageTimer.current);
        }
        setQldCenterMessage({ text, type });
        qldCenterMessageTimer.current = window.setTimeout(() => setQldCenterMessage(null), 2400);
    }, []);

    useEffect(() => () => {
        if (qldCenterMessageTimer.current) {
            window.clearTimeout(qldCenterMessageTimer.current);
        }
    }, []);

    const effectiveTrainerId = userData?.role === 'trainer'
        ? userData.trainerId
        : (trainerFilter?.value || '');

    useEffect(() => {
        const loadLinks = async () => {
            let constraints = [orderBy('createdAt', 'desc')];
            if (effectiveTrainerId) {
                constraints = [where('trainerId', '==', effectiveTrainerId), ...constraints];
            }

            const snapshot = await getDocs(query(collection(db, 'qldLinks'), ...constraints));
            const rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
            setLinks(rows);
            if (!rows.length) {
                setSelectedLinkId('');
                setSelectedStudentId('');
                return;
            }

            if (!selectedLinkId || !rows.some((row) => row.id === selectedLinkId)) {
                setSelectedLinkId(rows[0].id);
            }
        };

        loadLinks();
    }, [effectiveTrainerId, selectedLinkId]);

    useEffect(() => {
        setSelectedStudentId('');
        setShowStageReviewModal(false);
    }, [selectedLinkId]);

    useEffect(() => {
        if (!createSignal) {
            return;
        }

        if (userData?.role === 'admin' && !effectiveTrainerId) {
            onCreateHandled && onCreateHandled();
            return;
        }

        const createLink = async () => {
            const payload = buildQldLinkPayload(effectiveTrainerId);
            await setDoc(doc(db, 'qldLinks', payload.linkId), payload);
            await navigator.clipboard.writeText(payload.url);
            showQldCenterMessage('QLD student link copied to clipboard.');
            setSelectedLinkId(payload.linkId);
            onCreateHandled && onCreateHandled();
        };

        createLink();
    }, [createSignal, effectiveTrainerId, onCreateHandled, showQldCenterMessage]);

    useEffect(() => {
        if (!effectiveTrainerId) {
            setStudentCountsByLink({});
            return undefined;
        }

        const unsubscribe = onSnapshot(query(collection(db, 'qldStudents'), where('trainerId', '==', effectiveTrainerId)), (snapshot) => {
            const counts = snapshot.docs.reduce((result, studentDoc) => {
                const linkId = studentDoc.data()?.linkId;
                if (linkId) {
                    result[linkId] = (result[linkId] || 0) + 1;
                }
                return result;
            }, {});
            setStudentCountsByLink(counts);
        });

        return () => unsubscribe();
    }, [effectiveTrainerId]);

    useEffect(() => {
        if (!selectedLinkId) {
            return undefined;
        }

        const unsubscribe = onSnapshot(query(collection(db, 'qldStudents'), where('linkId', '==', selectedLinkId)), (snapshot) => {
            const rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
            setStudents(rows);
        });

        return () => unsubscribe();
    }, [selectedLinkId, selectedStudentId]);

    const selectedStudent = useMemo(() => students.find((row) => row.id === selectedStudentId), [students, selectedStudentId]);
    const gcitEligibleStudents = useMemo(
        () => students.filter((student) => canGenerateStudentPdf(student)),
        [students]
    );
    const qldCardEligibleStudents = useMemo(
        () => students.filter((student) => canGenerateQldCardForStudent(student)),
        [students]
    );
    const qldCardStudentsReadyForPdf = useMemo(
        () => qldCardEligibleStudents.filter((student) => hasQldCardSignature(student)),
        [qldCardEligibleStudents]
    );
    const qldCardStudentsMissingSignature = useMemo(
        () => qldCardEligibleStudents.filter((student) => !hasQldCardSignature(student)),
        [qldCardEligibleStudents]
    );
    const qldCardStudentsMissingNumbers = useMemo(
        () => qldCardStudentsReadyForPdf.filter((student) => !student.cardNumber),
        [qldCardStudentsReadyForPdf]
    );
    const selectedStage = selectedStudent?.stages?.[selectedStageKey] || null;
    const selectedStageCanOpen = Boolean(
        selectedStudent
        && (
            selectedStage?.completed
            || selectedStage?.reviewStatus
            || selectedStudent.responses?.[selectedStageKey]
            || selectedStudent.trainerResponses?.[selectedStageKey]
        )
    );
    const closeSelectedStage = () => {
        setSelectedStudentId(null);
    };

    const handleUnlockStage = async (student, stageKey) => {
        await updateDoc(doc(db, 'qldStudents', student.id), buildStageUnlockPatch(student, stageKey));
    };

    const canBulkReleaseStageForStudent = (student, stageKey) => {
        if (!student?.id || !student?.stages) {
            return false;
        }

        const stage = student.stages?.[stageKey];
        if (stage?.unlocked) {
            return false;
        }

        const stageIndex = QLD_STAGE_KEYS.indexOf(stageKey);
        if (stageIndex === -1) {
            return false;
        }

        if (stageIndex === 0) {
            return true;
        }

        const previousStageKey = QLD_STAGE_KEYS[stageIndex - 1];
        const previousStage = student.stages?.[previousStageKey];
        return Boolean(previousStage?.completed);
    };

    const getBulkReleaseEligibleStudents = (stageKey) => (
        students.filter((student) => canBulkReleaseStageForStudent(student, stageKey))
    );

    const handleBulkReleaseStage = async (stageKey) => {
        const eligibleStudents = getBulkReleaseEligibleStudents(stageKey);
        if (eligibleStudents.length === 0) {
            return;
        }

        setBulkReleasingStageKey(stageKey);
        try {
            await Promise.all(
                eligibleStudents.map((student) => (
                    updateDoc(doc(db, 'qldStudents', student.id), buildStageUnlockPatch(student, stageKey))
                ))
            );

            showQldCenterMessage(`${QLD_STAGE_LABELS[stageKey]} released for ${eligibleStudents.length} student${eligibleStudents.length === 1 ? '' : 's'}.`);
        } finally {
            setBulkReleasingStageKey('');
        }
    };

    const handleStageSave = async (stageKey, responseData, markComplete = false, meta = {}) => {
        if (!selectedStudent) {
            return;
        }

        if (!markComplete && areEqual(selectedStudent.responses?.[stageKey], responseData)) {
            return;
        }

        if (markComplete && stageKey === 'lln') {
            const decision = meta?.decision || 'satisfactory';
            const nextStages = decision === 'not_satisfactory'
                ? {
                    ...lockStagesFrom(selectedStudent.stages || {}, 'eoi'),
                    lln: {
                        ...(selectedStudent.stages?.lln || {}),
                        unlocked: true,
                        completed: false,
                        completedAt: '',
                        trainerReviewed: true,
                        reviewStatus: 'not_satisfactory',
                    },
                }
                : {
                    ...(selectedStudent.stages || {}),
                    lln: {
                        ...(selectedStudent.stages?.lln || {}),
                        completed: true,
                        completedAt: new Date().toISOString(),
                        trainerReviewed: true,
                        reviewStatus: 'passed',
                    },
                    eoi: {
                        ...(selectedStudent.stages?.eoi || {}),
                        unlocked: true,
                    },
                };

            await updateDoc(doc(db, 'qldStudents', selectedStudent.id), {
                responses: {
                    ...(selectedStudent.responses || {}),
                    [stageKey]: responseData,
                },
                stages: nextStages,
                auditLog: [
                    ...(selectedStudent.auditLog || []),
                    buildQldAuditEvent('llnd_reviewed', { decision }),
                ],
                updatedAt: new Date().toISOString(),
            });
            showQldCenterMessage('LLND saved.');
            closeSelectedStage();
            return;
        }

        if (markComplete && stageKey === 'eoi') {
            const decision = meta?.decision || 'verified';
            const nextStages = decision === 'cannot_verify'
                ? {
                    ...lockStagesFrom(selectedStudent.stages || {}, 'assessment1'),
                    eoi: {
                        ...(selectedStudent.stages?.eoi || {}),
                        completed: true,
                        completedAt: new Date().toISOString(),
                        trainerReviewed: true,
                        reviewStatus: 'cannot_verify',
                    },
                }
                : {
                    ...(selectedStudent.stages || {}),
                    eoi: {
                        ...(selectedStudent.stages?.eoi || {}),
                        completed: true,
                        completedAt: new Date().toISOString(),
                        trainerReviewed: true,
                        reviewStatus: 'passed',
                    }
                };

            showLoadingMessage(decision === 'verified' ? 'Marking Student as Verified' : 'Marking Student as Not Verified');
            try {
                await updateDoc(doc(db, 'qldStudents', selectedStudent.id), {
                    ...(isValidQldMobile(responseData.mobile) ? { mobile: normalizeQldMobile(responseData.mobile) } : {}),
                    responses: {
                        ...(selectedStudent.responses || {}),
                        [stageKey]: responseData,
                    },
                    stages: nextStages,
                    auditLog: [
                        ...(selectedStudent.auditLog || []),
                        buildQldAuditEvent('eoi_reviewed', { decision }),
                    ],
                    updatedAt: new Date().toISOString(),
                });
                showQldCenterMessage('EOI saved.');
                showSuccessMessage(decision === 'verified' ? 'Student marked as Verified' : 'Student marked as Not Verified');
                closeSelectedStage();
            } catch (error) {
                clearPageMessage();
                showWarningMessage('Student verification could not be updated. Please try again.');
                console.error('QLD EOI verification update failed', error);
            }
            return;
        }

        if (markComplete && stageKey === 'declaration') {
            const decision = meta?.decision || 'passed';

            await updateDoc(doc(db, 'qldStudents', selectedStudent.id), {
                responses: {
                    ...(selectedStudent.responses || {}),
                    [stageKey]: responseData,
                },
                stages: {
                    ...(selectedStudent.stages || {}),
                    declaration: {
                        ...(selectedStudent.stages?.declaration || {}),
                        unlocked: true,
                        completed: true,
                        completedAt: (selectedStudent.stages?.declaration?.completedAt) || new Date().toISOString(),
                        trainerReviewed: true,
                        reviewStatus: decision,
                    },
                },
                auditLog: [
                    ...(selectedStudent.auditLog || []),
                    buildQldAuditEvent('declaration_reviewed', { decision }),
                ],
                updatedAt: new Date().toISOString(),
            });
            showQldCenterMessage('Declaration marked complete.');
            closeSelectedStage();
            return;
        }

        if (markComplete && isQldAssessmentStage(stageKey)) {
            const manualQuestionIndexes = getAssessmentReviewQuestionIndexes(selectedStudent, stageKey);
            const currentReview = meta?.assessorReview || selectedStudent.trainerResponses?.[stageKey] || { questionMarks: {} };
            const currentQuestionMarks = currentReview?.questionMarks || {};
            const missingMarks = manualQuestionIndexes.filter((questionIndex) => (
                getQuestionReviewState(assessmentQuestionMap[stageKey]?.[questionIndex], questionIndex, responseData, currentReview).missing
            ));

            if (missingMarks.length > 0) {
                const questionNumbers = missingMarks
                    .map((questionIndex) => assessmentQuestionMap[stageKey]?.[questionIndex]?.id)
                    .filter(Boolean);
                showQldCenterMessage(`Please mark question${questionNumbers.length > 1 ? 's' : ''} ${questionNumbers.join(', ')} before submitting this review.`, 'error');
                return;
            }

            const sanitizedQuestionMarks = Object.keys(currentQuestionMarks).reduce((acc, key) => {
                const existingValue = currentQuestionMarks[key] || {};
                acc[key] = {
                    ...existingValue,
                    comment: existingValue.mark === 'NYC' ? (existingValue.comment || '') : '',
                };
                return acc;
            }, {});

            const sanitizedReview = {
                ...(currentReview || {}),
                questionMarks: sanitizedQuestionMarks,
            };

            const incorrectQuestionIndexes = manualQuestionIndexes.filter((questionIndex) => (
                getQuestionReviewState(assessmentQuestionMap[stageKey]?.[questionIndex], questionIndex, responseData, sanitizedReview).mark === 'NYC'
            ));

            const sanitizedResponses = Object.keys(responseData?.responses || {}).reduce((acc, key) => {
                const questionIndex = Number(key);
                const question = assessmentQuestionMap[stageKey]?.[questionIndex];
                const questionResponse = responseData?.responses?.[key];

                if (!hasEmbeddedAssessorControls(question)) {
                    acc[key] = questionResponse;
                    return acc;
                }

                const questionReviewState = getQuestionReviewState(question, questionIndex, responseData, sanitizedReview);
                acc[key] = questionReviewState.mark === 'NYC'
                    ? questionResponse
                    : { ...(questionResponse || {}), assessorComments: '' };
                return acc;
            }, {});

            const nextResponseData = {
                ...(responseData || {}),
                responses: sanitizedResponses,
                hasCompletedAssessment: incorrectQuestionIndexes.length === 0,
                answeringIncorrect: false,
                manualRetryQuestionIndexes: incorrectQuestionIndexes,
                finalQIndex: incorrectQuestionIndexes.length > 0
                    ? incorrectQuestionIndexes[0]
                    : (responseData?.finalQIndex || 0),
            };

            await updateDoc(doc(db, 'qldStudents', selectedStudent.id), {
                responses: {
                    ...(selectedStudent.responses || {}),
                    [stageKey]: nextResponseData,
                },
                trainerResponses: {
                    ...(selectedStudent.trainerResponses || {}),
                    [stageKey]: sanitizedReview,
                },
                stages: {
                    ...(selectedStudent.stages || {}),
                    [stageKey]: {
                        ...(selectedStudent.stages?.[stageKey] || {}),
                        unlocked: true,
                        completed: incorrectQuestionIndexes.length === 0,
                        completedAt: incorrectQuestionIndexes.length === 0
                            ? ((selectedStudent.stages?.[stageKey]?.completedAt) || new Date().toISOString())
                            : '',
                        trainerReviewed: true,
                        reviewStatus: incorrectQuestionIndexes.length === 0 ? 'passed' : 'needs_resubmission',
                    }
                },
                updatedAt: new Date().toISOString(),
            });

            showQldCenterMessage(
                incorrectQuestionIndexes.length === 0
                    ? `${QLD_STAGE_LABELS[stageKey]} marked complete.`
                    : `${QLD_STAGE_LABELS[stageKey]} responses submitted for Student to review.`
            );
            closeSelectedStage();
            return;
        }

        const nextPatch = markComplete
            ? {
                responses: {
                    ...(selectedStudent.responses || {}),
                    [stageKey]: responseData,
                },
                stages: {
                    ...(selectedStudent.stages || {}),
                    [stageKey]: {
                        ...(selectedStudent.stages?.[stageKey] || {}),
                        completed: true,
                        completedAt: new Date().toISOString(),
                    }
                },
                updatedAt: new Date().toISOString(),
            }
            : {
                responses: {
                    ...(selectedStudent.responses || {}),
                    [stageKey]: responseData,
                },
                updatedAt: new Date().toISOString(),
            };

        if (stageKey === 'eoi' && isValidQldMobile(responseData.mobile)) {
            nextPatch.mobile = normalizeQldMobile(responseData.mobile);
        }
        await updateDoc(doc(db, 'qldStudents', selectedStudent.id), nextPatch);
        if (markComplete) {
            closeSelectedStage();
        }
    };

    const handleAssessorReviewSave = async (stageKey, reviewData) => {
        if (!selectedStudent) {
            return;
        }

        if (areEqual(selectedStudent.trainerResponses?.[stageKey], reviewData)) {
            return;
        }

        await updateDoc(doc(db, 'qldStudents', selectedStudent.id), {
            trainerResponses: {
                ...(selectedStudent.trainerResponses || {}),
                [stageKey]: reviewData,
            },
            updatedAt: new Date().toISOString(),
        });
    };

    const handleGeneratePdf = async (student) => {
        if (!student || !canGenerateStudentPdf(student)) {
            return;
        }

        setGeneratingPdfStudentId(student.id);

        try {
            const response = await axios.post(generateQldAssessmentPdfUrl, {
                studentId: student.id,
            });

            showQldCenterMessage('QLD assessment PDF generated.');

            await updateDoc(doc(db, 'qldStudents', student.id), {
                generatedPdf: response.data?.pdfUrl || '',
                generatedPdfAt: new Date().toISOString(),
            });
        } catch (error) {
            console.error('Failed to generate QLD PDF', error);
            showQldCenterMessage(error?.response?.data?.error || 'Unable to generate QLD assessment PDF.', 'error');
        } finally {
            setGeneratingPdfStudentId('');
        }
    };

    const fetchAvailableQldCardNumbers = async () => {
        const requiredCount = qldCardStudentsMissingNumbers.length;
        if (requiredCount <= 0) {
            setAvailableQldCardNumbers([]);
            return [];
        }

        setIsLoadingQldCardNumbers(true);
        try {
            const snapshot = await getDocs(query(
                collection(db, 'qld-student-numbers'),
                where('available', '==', true),
                orderBy('number', 'asc'),
                limit(requiredCount),
            ));
            const rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
            setAvailableQldCardNumbers(rows);
            return rows;
        } finally {
            setIsLoadingQldCardNumbers(false);
        }
    };

    useEffect(() => {
        if (!showGenerateCardsModal) {
            return;
        }

        fetchAvailableQldCardNumbers();
    }, [showGenerateCardsModal, qldCardStudentsMissingNumbers.length]);

    const handleCreateQldCardNumbers = async () => {
        const minValue = parseInt(String(minCardNo || '').trim(), 10);
        const maxValue = parseInt(String(maxCardNo || '').trim(), 10);

        if (!Number.isInteger(minValue) || !Number.isInteger(maxValue) || maxValue < minValue) {
            showQldCenterMessage('Enter a valid minimum and maximum card number range.', 'error');
            return;
        }

        setIsCreatingQldCardNumbers(true);
        try {
            const writes = [];
            for (let number = minValue; number <= maxValue; number += 1) {
                writes.push(
                    setDoc(doc(collection(db, 'qld-student-numbers'), String(number)), {
                        available: true,
                        number,
                    }, { merge: true })
                );
            }
            await Promise.all(writes);
            showQldCenterMessage(`Added ${maxValue - minValue + 1} QLD student card number${maxValue - minValue === 0 ? '' : 's'}.`);
            setShowGenerateCardNumbersModal(false);
            setMinCardNo('');
            setMaxCardNo('');
            if (showGenerateCardsModal) {
                await fetchAvailableQldCardNumbers();
            }
        } finally {
            setIsCreatingQldCardNumbers(false);
        }
    };

    const handleGenerateQldCards = async () => {
        if (qldCardStudentsReadyForPdf.length === 0) {
            showQldCenterMessage('No completed students with declaration signatures are available for card generation.', 'error');
            return;
        }

        if (availableQldCardNumbers.length < qldCardStudentsMissingNumbers.length) {
            showQldCenterMessage('You do not have sufficient QLD card numbers. Please generate more first.', 'error');
            return;
        }

        setIsGeneratingQldCards(true);
        try {
            const response = await axios.post(generateQldCardsUrl, {
                data: {
                    availableStudentNos: availableQldCardNumbers.map(({ number }) => number),
                    selectedRows: qldCardStudentsReadyForPdf.map((student) => ({
                        id: student.id,
                        studentName: toTitleCase(buildQldFullName(student)),
                        dob: student.dob,
                        createdDate: student.responses?.declaration?.studentDeclarationDate
                            || student.stages?.declaration?.completedAt
                            || student.updatedAt
                            || student.createdAt,
                        signature: student.responses?.declaration?.studentSignature || '',
                        studentCardNo: student.cardNumber || '',
                    })),
                }
            });

            const urls = Array.isArray(response.data) ? response.data.filter(Boolean) : [];
            urls.forEach((url) => window.open(url, '_blank', 'noopener,noreferrer'));

            showQldCenterMessage(`Generated QLD student card PDF${urls.length === 1 ? '' : 's'} for ${qldCardStudentsReadyForPdf.length} student${qldCardStudentsReadyForPdf.length === 1 ? '' : 's'}.`);
            setShowGenerateCardsModal(false);
            setAvailableQldCardNumbers([]);
        } catch (error) {
            console.error('Failed to generate QLD cards', error);
            showQldCenterMessage(error?.response?.data?.error || 'Unable to generate QLD student cards.', 'error');
        } finally {
            setIsGeneratingQldCards(false);
        }
    };

    const handleGenerateGcitReport = async () => {
        if (isGeneratingGcitReport) {
            return;
        }

        setIsGeneratingGcitReport(true);
        try {
            const { blob, fileName, eligibleStudents } = await generateGcitReportTemplate({
                students: gcitEligibleStudents,
                canGenerateStudentPdf,
            });

            const downloadUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = downloadUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(downloadUrl);

            showQldCenterMessage(`GCIT report template generated for ${eligibleStudents.length} student${eligibleStudents.length === 1 ? '' : 's'}.`);
        } catch (error) {
            console.error('Failed to generate GCIT report template', error);
            showQldCenterMessage(error?.message || 'Unable to generate the GCIT report template.', 'error');
        } finally {
            setIsGeneratingGcitReport(false);
        }
    };

    useEffect(() => {
        onToolbarStateChange?.({
            canGenerateGcit: gcitEligibleStudents.length > 0,
            isGeneratingGcit: isGeneratingGcitReport,
        });
    }, [gcitEligibleStudents.length, isGeneratingGcitReport, onToolbarStateChange]);

    useEffect(() => {
        if (studentNumbersSignal && studentNumbersSignal !== lastStudentNumbersSignal.current) {
            lastStudentNumbersSignal.current = studentNumbersSignal;
            setShowGenerateCardNumbersModal(true);
        }
    }, [studentNumbersSignal]);

    useEffect(() => {
        if (gcitSignal && gcitSignal !== lastGcitSignal.current) {
            lastGcitSignal.current = gcitSignal;
            handleGenerateGcitReport();
        }
    }, [gcitSignal]);

    return (
        <div className='qldDashboardLayout' style={{ display: 'flex', gap: '20px', width: '100%' }}>
            {qldCenterMessage && (
                <div className={`qldCenterScreenMessage ${qldCenterMessage.type === 'error' ? 'error' : 'success'} ${qldCenterMessage.text === 'LLND saved.' ? 'llndSaved' : ''}`} role='status' aria-live='polite'>
                    <span className='qldCenterScreenMessageIcon' aria-hidden='true'>{qldCenterMessage.type === 'error' ? '×' : '✓'}</span>
                    <span className='qldCenterScreenMessageText'>
                        {qldCenterMessage.text === 'LLND saved.' && <strong>Saved successfully</strong>}
                        {qldCenterMessage.text}
                    </span>
                    <button type='button' className='qldCenterScreenMessageClose' onClick={() => setQldCenterMessage(null)}>Close</button>
                </div>
            )}
            <div className='sidebarList dashboardDataPanel qldCoursesPanel' style={{ maxWidth: '300px' }}>
                <div className='dashboardDataPanelHeader qldCoursesHeader coursesPanelHeader'>
                    <div className='coursesPanelTitleRow'>
                        <span className='coursesPanelIcon' aria-hidden='true'>
                            <svg width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='m4 19.5 8-4 8 4'/><path d='M4 4.5v15'/><path d='M20 4.5v15'/><path d='M4 4.5h16v11H4z'/></svg>
                        </span>
                        <div>
                            <h3>Courses</h3>
                            <p>Choose a course to view its students</p>
                        </div>
                    </div>
                    <button
                        type='button'
                        className='qldCreateStudentLink'
                        disabled={userData?.role === 'admin' && !effectiveTrainerId}
                        title={userData?.role === 'admin' && !effectiveTrainerId ? 'Select a trainer before creating a QLD student link.' : ''}
                        onClick={onCreateStudentLink}
                    >
                        <svg aria-hidden='true' width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                            <circle cx='9' cy='8' r='4'/>
                            <path d='M2 21a7 7 0 0 1 14 0'/>
                            <path d='M19 8v6M16 11h6'/>
                        </svg>
                        Create Student Link
                    </button>
                    {userData?.role === 'admin' && (
                        <div className='trainerFilterDropdown coursesTrainerFilter qldTrainerFilter'>
                            <Dropdown
                                clearable
                                options={trainerDirectory.map((trainer) => ({
                                    label: trainer.name.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()),
                                    value: trainer.trainerId,
                                }))}
                                value={trainerFilter}
                                setValue={onTrainerFilterChange}
                                label='Select Trainer'
                                customStyle={{ marginTop: 0, marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                                errorFields={[]}
                                name='qldTrainerFilter'
                            />
                        </div>
                    )}
                </div>
                {links.map((link) => (
                    <div
                        key={link.id}
                        className={`sidebarRow ${selectedLinkId === link.id ? 'activeRow' : ''}`}
                        onClick={() => setSelectedLinkId(link.id)}>
                            <div className='qldCourseDetails'>
                                <div className='qldCourseDetailRow qldCourseDateRow'>
                                    <svg aria-hidden='true' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='3' y='4' width='18' height='18' rx='2'/><path d='M16 2v4'/><path d='M8 2v4'/><path d='M3 10h18'/></svg>
                                    <span>{formatQldCourseDate(link)}</span>
                                </div>
                                <div className='qldCourseDetailRow'>
                                    <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='8' r='4'/><path d='M4 21a8 8 0 0 1 16 0'/></svg>
                                    <span>{link.trainerName || trainerFilter?.label || trainerDirectory.find((trainer) => trainer.trainerId === link.trainerId)?.name || userData?.name || effectiveTrainerId}</span>
                                </div>
                                <div className='qldCourseDetailRow'>
                                    <svg aria-hidden='true' width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='7' r='3'/><circle cx='5' cy='9' r='2.25'/><circle cx='19' cy='9' r='2.25'/><path d='M7 20v-1a5 5 0 0 1 10 0v1'/><path d='M1.5 19v-.5A3.5 3.5 0 0 1 5 15h1'/><path d='M22.5 19v-.5A3.5 3.5 0 0 0 19 15h-1'/></svg>
                                    <span>{studentCountsByLink[link.id] || 0} Student{studentCountsByLink[link.id] === 1 ? '' : 's'}</span>
                                </div>
                                <div className='qldCourseDetailRow qldCourseDeliveryRow'>
                                    <div className='qldCourseDeliveryValue'>
                                        <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='3' y='4' width='18' height='14' rx='2'/><path d='M8 22h8M12 18v4'/></svg>
                                        <span>{link.delivery || 'Online'}</span>
                                    </div>
                                    <button
                                type='button'
                                className='qldCourseCopyPill'
                                aria-label='Copy course link'
                                title='Copy course link'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    copyCourseLink(link.url).then(() => showQldCenterMessage('Course link copied to clipboard.'));
                                }}
                            >
                                <svg aria-hidden='true' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='9' y='9' width='13' height='13' rx='2'/><path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'/></svg>
                                Course Link
                                    </button>
                                </div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ flex: 1 }}>
                {selectedLinkId && (
                    <div style={qldTableShellStyles}>
                        <div className='dashboardDataPanelHeader qldPipelineHeader' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', padding: '14px 16px', borderBottom: '1px solid #e4e8ec' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '19px', color: '#0f1720', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span aria-hidden='true' style={{ display: 'inline-flex', color: '#ca8a04' }}>
                                        <svg width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='7' r='3'/><circle cx='5' cy='9' r='2.25'/><circle cx='19' cy='9' r='2.25'/><path d='M7 20v-1a5 5 0 0 1 10 0v1'/><path d='M1.5 19v-.5A3.5 3.5 0 0 1 5 15h1'/><path d='M22.5 19v-.5A3.5 3.5 0 0 0 19 15h-1'/></svg>
                                    </span>
                                    Student Pipeline
                                </h3>
                                <p style={{ margin: '6px 0 0', color: '#64707d', fontSize: '14px' }}>
                                    Track progress, reopen completed submissions, and generate PDFs once every stage is complete.
                                </p>
                            </div>
                        </div>
                        <div className='qldPipelineTableWrap' style={{ overflowX: 'auto' }}>
                            <table className='qldPipelineTable' style={{ width: '100%', minWidth: '980px', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...qldTableHeaderCellStyles, textAlign: 'left', paddingLeft: '16px', minWidth: '180px' }}>
                                            Student Name
                                        </th>
                                        {QLD_STAGE_KEYS.map((stageKey) => (
                                            <th key={stageKey} style={qldTableHeaderCellStyles}>{QLD_STAGE_LABELS[stageKey]}</th>
                                        ))}
                                        <th style={{ ...qldTableHeaderCellStyles, whiteSpace: 'normal', lineHeight: 1.2 }}>Assessment<br />Book</th>
                                    </tr>
                                    <tr className='qldGroupReleaseRow'>
                                        <th aria-label='Group Release' style={{ ...qldTableHeaderCellStyles, textAlign: 'left', paddingLeft: '16px', background: '#ffffff', borderTop: '1px solid #edf1f4' }} />
                                        {QLD_STAGE_KEYS.map((stageKey) => {
                                            const eligibleCount = getBulkReleaseEligibleStudents(stageKey).length;
                                            const isReleasing = bulkReleasingStageKey === stageKey;

                                            return (
                                                <th key={`${stageKey}-release`} style={{ ...qldTableHeaderCellStyles, background: '#ffffff', borderTop: '1px solid #edf1f4' }}>
                                                    <button
                                                        type='button'
                                                        onClick={() => handleBulkReleaseStage(stageKey)}
                                                        disabled={eligibleCount === 0 || isReleasing}
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderRadius: '999px',
                                                            border: `1px solid ${eligibleCount === 0 ? '#dce3e8' : '#c9d2db'}`,
                                                            background: eligibleCount === 0 ? '#f5f7f9' : '#fff',
                                                            color: eligibleCount === 0 ? '#98a2ad' : '#324152',
                                                            fontSize: '12px',
                                                            fontWeight: '700',
                                                            cursor: eligibleCount === 0 || isReleasing ? 'not-allowed' : 'pointer',
                                                            minWidth: '78px',
                                                        }}
                                                        title={eligibleCount === 0 ? 'No eligible students to release.' : `Release ${QLD_STAGE_LABELS[stageKey]} for ${eligibleCount} eligible student${eligibleCount === 1 ? '' : 's'}.`}
                                                    >
                                                        {isReleasing ? 'Releasing...' : `Release All${eligibleCount > 0 ? ` (${eligibleCount})` : ''}`}
                                                    </button>
                                                </th>
                                            );
                                        })}
                                        <th style={{ ...qldTableHeaderCellStyles, background: '#ffffff', borderTop: '1px solid #edf1f4' }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((student, studentIndex) => (
                                        <tr
                                            key={student.id}
                                            style={{
                                                backgroundColor: '#fff',
                                                boxShadow: 'none',
                                            }}
                                        >
                                            <td style={{ padding: '8px 8px 8px 16px', borderBottom: studentIndex === students.length - 1 ? 'none' : '1px solid #edf1f4', color: '#000', verticalAlign: 'middle' }}>
                                                <button
                                                    type='button'
                                                    style={{ border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', padding: 0, color: '#0f1720', fontSize: '13px', fontWeight: '400' }}
                                                    onClick={() => copyStudentLink(student).then(() => showQldCenterMessage('Student link copied to clipboard.'))}
                                                >
                                                    {toTitleCase(buildQldFullName(student))}
                                                </button>
                                                <small style={{ display: 'block', fontWeight: 400 }}>Mobile: {getQldMobile(student) || 'Not provided'}</small>
                                                {student.underMinimumAge && (
                                                    <div style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                        <span style={{ padding: '5px 10px', borderRadius: '999px', background: '#ffe5e5', color: '#a12828', fontSize: '12px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Under 13</span>
                                                    </div>
                                                )}
                                            </td>
                                            {QLD_STAGE_KEYS.map((stageKey, index) => {
                                                const stage = student.stages?.[stageKey];
                                                const previousStage = index === 0 ? null : student.stages?.[QLD_STAGE_KEYS[index - 1]];
                                                const canUnlock = !stage?.unlocked && previousStage?.completed;
                                                const isStagePassed = stage?.reviewStatus === 'passed';
                                                const isStageSubmitted = stage?.reviewStatus === 'submitted_for_review' || stage?.reviewStatus === 'student_submitted';
                                                const isStageUnsatisfactory = stage?.reviewStatus === 'not_satisfactory'
                                                    || stage?.reviewStatus === 'cannot_verify'
                                                    || stage?.reviewStatus === 'unsatisfactory';
                                                const hasSavedStageDocument = Boolean(
                                                    student.responses?.[stageKey]
                                                    || student.trainerResponses?.[stageKey]
                                                );
                                                const canOpenStage = Boolean(
                                                    stage?.completed
                                                    || isStagePassed
                                                    || isStageSubmitted
                                                    || isStageUnsatisfactory
                                                    || hasSavedStageDocument
                                                );
                                                const hasIconStatus = isStagePassed || isStageSubmitted || isStageUnsatisfactory;

                                                return (
                                                    <td
                                                        key={stageKey}
                                                        style={{
                                                            padding: '8px 6px',
                                                            textAlign: 'center',
                                                            verticalAlign: 'middle',
                                                            borderBottom: studentIndex === students.length - 1 ? 'none' : '1px solid #edf1f4',
                                                            background: 'transparent',
                                                            ...getStageCellStyles(stage),
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: hasIconStatus ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: hasIconStatus ? '8px' : '4px' }}>
                                                             <button
                                                                 type='button'
                                                                 aria-label={`View ${QLD_STAGE_LABELS[stageKey]} document for ${student.name}`}
                                                                 title={canOpenStage ? 'View document' : 'Document not yet available'}
                                                                 onClick={() => {
                                                                    if (canOpenStage) {
                                                                        setSelectedStudentId(student.id);
                                                                        setSelectedStageKey(stageKey);
                                                                        setShowStageReviewModal(true);
                                                                    }
                                                                }}
                                                                disabled={!canOpenStage}
                                                                style={{
                                                                    ...qldStageBadgeBaseStyles,
                                                                     ...getStageBadgeStyles(stage),
                                                                     ...(hasIconStatus ? {
                                                                         width: '30px',
                                                                         minWidth: '30px',
                                                                         minHeight: '30px',
                                                                        padding: 0,
                                                                        borderRadius: '9px',
                                                                        background: '#ffffff',
                                                                    } : {}),
                                                                    cursor: canOpenStage ? 'pointer' : 'default',
                                                                    transform: canOpenStage ? 'translateY(0)' : 'none',
                                                                    opacity: canOpenStage ? 1 : 0.92,
                                                                }}
                                                            >
                                                                {hasIconStatus ? (
                                                                     <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                                                                        <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z'/>
                                                                        <path d='M14 2v6h6M8 13h8M8 17h6'/>
                                                                    </svg>
                                                                ) : getStageStatusLabel(stage)}
                                                            </button>
                                                            {hasIconStatus && (
                                                                 <span
                                                                     className={`qldStageIconStatus ${isStagePassed ? 'complete' : isStageSubmitted ? 'submitted' : 'unsatisfactory'}`}
                                                                     role='img'
                                                                     aria-label={isStagePassed ? 'Completed' : isStageSubmitted ? 'Submitted' : 'Unsatisfactory'}
                                                                    title={isStagePassed ? 'Completed' : isStageSubmitted ? 'Submitted' : 'Unsatisfactory'}
                                                                >
                                                                     <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'>
                                                                        {isStagePassed
                                                                            ? <path d='m5 12 4 4L19 6'/>
                                                                            : isStageSubmitted
                                                                                ? <><path d='M9.6 9a2.7 2.7 0 1 1 4.3 2.2c-1.2.8-1.9 1.3-1.9 2.8'/><path d='M12 17h.01'/></>
                                                                                : <><path d='M18 6 6 18'/><path d='m6 6 12 12'/></>}
                                                                    </svg>
                                                                </span>
                                                            )}
                                                            {canUnlock && (
                                                                <button
                                                                    type='button'
                                                                    className='qldPipelineStatusPill'
                                                                    style={{
                                                                        border: '1px solid #c9d2db',
                                                                        background: '#fff',
                                                                        color: '#324152',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onClick={() => handleUnlockStage(student, stageKey)}
                                                                >
                                                                    Unlock
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle', borderBottom: studentIndex === students.length - 1 ? 'none' : '1px solid #edf1f4', background: 'transparent' }}>
                                                {student.generatedPdf ? (
                                                    <a
                                                        href={student.generatedPdf}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '96px',
                                                            padding: '8px 10px',
                                                            borderRadius: '12px',
                                                            background: '#eef6ff',
                                                            border: '1px solid #cfe0f5',
                                                            color: '#0b4b8a',
                                                            fontWeight: '700',
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        Download
                                                    </a>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        className='qldPipelineStatusPill dashboardActionPill'
                                                        onClick={() => handleGeneratePdf(student)}
                                                        disabled={!canGenerateStudentPdf(student) || generatingPdfStudentId === student.id}
                                                        style={{
                                                            border: `1px solid ${!canGenerateStudentPdf(student) ? '#dde3e8' : '#cbd5e1'}`,
                                                            background: !canGenerateStudentPdf(student)
                                                                ? '#f5f7f9'
                                                                : '#ffffff',
                                                            color: !canGenerateStudentPdf(student)
                                                                ? '#94a0ad'
                                                                : '#374151',
                                                            cursor: !canGenerateStudentPdf(student) || generatingPdfStudentId === student.id
                                                                ? 'not-allowed'
                                                                : 'pointer',
                                                            opacity: generatingPdfStudentId === student.id ? 0.7 : 1,
                                                            boxShadow: !canGenerateStudentPdf(student) ? 'none' : '0 2px 5px rgba(15, 23, 42, 0.08)',
                                                        }}
                                                        title={!canGenerateStudentPdf(student) ? 'Complete all sections before generating the PDF.' : ''}
                                                    >
                                                        {generatingPdfStudentId === student.id ? 'Generating...' : 'Generate'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className='qldDashboardBottomActions'>
                            <span className='qldDashboardStudentCount'>
                                <svg aria-hidden='true' width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='7' r='3'/><circle cx='5' cy='9' r='2.25'/><circle cx='19' cy='9' r='2.25'/><path d='M7 20v-1a5 5 0 0 1 10 0v1'/><path d='M1.5 19v-.5A3.5 3.5 0 0 1 5 15h1'/><path d='M22.5 19v-.5A3.5 3.5 0 0 0 19 15h-1'/></svg>
                                <strong>{students.length}</strong>
                                <span>students</span>
                            </span>
                            <div className='qldDashboardBottomActionPills'>
                            <button
                                type='button'
                                className='dashboardActionPill qldGenerateCardsButton'
                                onClick={() => setShowGenerateCardsModal(true)}
                                disabled={qldCardStudentsReadyForPdf.length === 0}
                                style={{
                                    background: qldCardStudentsReadyForPdf.length === 0 ? '#f5f7f9' : '#ffffff',
                                    color: qldCardStudentsReadyForPdf.length === 0 ? '#94a0ad' : '#374151',
                                    cursor: qldCardStudentsReadyForPdf.length === 0 ? 'not-allowed' : 'pointer',
                                    boxShadow: qldCardStudentsReadyForPdf.length === 0 ? 'none' : '0 2px 5px rgba(15, 23, 42, 0.08)',
                                }}
                                title={qldCardStudentsReadyForPdf.length === 0 ? 'At least one completed student with a declaration signature is required before generating class cards.' : ''}
                            >
                                <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/><path d='M14 2v6h6'/><path d='M8 13h8'/><path d='M8 17h5'/></svg>
                                Generate Student Cards PDF
                            </button>
                            </div>
                        </div>
                    </div>
                )}

                <ModalComponent
                    showModal={showStageReviewModal && selectedStageCanOpen}
                    setShowModal={setShowStageReviewModal}
                    contentClassName='qldStageReviewModal'
                    contentStyle={{ width: '90%', maxWidth: '1000px', maxHeight: '88vh', overflowY: 'auto', padding: 0, border: 'none', borderRadius: '14px', background: '#f8fafc', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)' }}
                >
                    {selectedStageCanOpen && (
                        <>
                            <div className='qldStageModalHeader'>
                                <div>
                                    <h2>{QLD_STAGE_LABELS[selectedStageKey]}</h2>
                                    <p className='qldStageModalStudent'><strong>Student:</strong> {toTitleCase(buildQldFullName(selectedStudent))}</p>
                                </div>
                                <div className='qldStageModalHeaderActions'>
                                    <button className='qldStageModalClose' type='button' onClick={() => setShowStageReviewModal(false)}>Close</button>
                                    {(isQldAssessmentStage(selectedStageKey) || selectedStageKey === 'lln') && (
                                        <div className='qldStageModalDecisionRow'>
                                            <button
                                                className='qldStageDecisionSatisfactory'
                                                type='button'
                                                onClick={() => handleStageSave(
                                                    selectedStageKey,
                                                    selectedStudent.responses?.[selectedStageKey] || {},
                                                    true,
                                                    { assessorReview: selectedStudent.trainerResponses?.[selectedStageKey] || { questionMarks: {} }, decision: 'satisfactory' }
                                                )}
                                            >
                                                <svg aria-hidden='true' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='m5 12 4 4L19 6'/></svg>
                                                Satisfactory
                                            </button>
                                            <button
                                                className='qldStageDecisionUnsatisfactory'
                                                type='button'
                                                onClick={() => handleStageSave(
                                                    selectedStageKey,
                                                    selectedStudent.responses?.[selectedStageKey] || {},
                                                    true,
                                                    { assessorReview: selectedStudent.trainerResponses?.[selectedStageKey] || { questionMarks: {} }, decision: 'not_satisfactory' }
                                                )}
                                            >
                                                <svg aria-hidden='true' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M18 6 6 18'/><path d='m6 6 12 12'/></svg>
                                                Unsatisfactory
                                            </button>
                                        </div>
                                    )}
                                    {selectedStageKey === 'eoi' && (
                                        <div className='qldStageModalDecisionRow'>
                                            <button
                                                className='qldStageDecisionSatisfactory'
                                                type='button'
                                                onClick={() => handleStageSave(selectedStageKey, selectedStudent.responses?.eoi || {}, true, { decision: 'verified' })}
                                            >
                                                <svg aria-hidden='true' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='m5 12 4 4L19 6'/></svg>
                                                ID Verified
                                            </button>
                                            <button
                                                className='qldStageDecisionUnsatisfactory'
                                                type='button'
                                                onClick={() => handleStageSave(selectedStageKey, selectedStudent.responses?.eoi || {}, true, { decision: 'cannot_verify' })}
                                            >
                                                <svg aria-hidden='true' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M18 6 6 18'/><path d='m6 6 12 12'/></svg>
                                                Not Verified
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='qldStageModalBody'>
                                <img className='qldStageModalLogo' src={Logo} alt='Upskill Institute' />
                                <QldStageRenderer
                                    key={`${selectedStudent.id}-${selectedStageKey}`}
                                    stageKey={selectedStageKey}
                                    studentDoc={selectedStudent}
                                    trainerMode
                                    hideTrainerDecisionActions
                                    onStageProgress={(responseData) => handleStageSave(selectedStageKey, responseData, false)}
                                    onStageComplete={(responseData, meta) => handleStageSave(selectedStageKey, responseData, true, meta)}
                                    assessorReviewData={selectedStudent.trainerResponses?.[selectedStageKey] || { questionMarks: {} }}
                                    onAssessorReviewChange={(reviewData) => handleAssessorReviewSave(selectedStageKey, reviewData)}
                                />
                            </div>
                        </>
                    )}
                </ModalComponent>

                <ModalComponent showModal={showGenerateCardNumbersModal} setShowModal={setShowGenerateCardNumbersModal} contentClassName='qldGenerationModal' contentStyle={{ maxWidth: '600px', padding: 0 }}>
                    <div className='qldGenerationModalInner'>
                        <div className='qldGenerationModalHeader'>
                            <span className='qldGenerationModalIcon'><svg aria-hidden='true' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='3' y='5' width='18' height='14' rx='2'/><path d='M7 9h4'/><path d='M7 13h7'/><path d='M17 9h.01'/></svg></span>
                            <div><h2>Generate Student Numbers</h2><p>Add a consecutive range of QLD student card numbers to the available pool.</p></div>
                        </div>
                        <div className='qldGenerationModalBody'>
                            <div className='qldGenerationInfo'><strong>Number range</strong><span>Enter the first and last card number. Every number in this range will be created.</span></div>
                            <div className='qldGenerationFields'>
                                <Input bold type='text' value={minCardNo} setValue={setMinCardNo} label='First Card Number' errors={[]} name='minCardNo' />
                                <Input bold type='text' value={maxCardNo} setValue={setMaxCardNo} label='Last Card Number' errors={[]} name='maxCardNo' />
                            </div>
                        </div>
                        <div className='qldGenerationModalActions'>
                            <button className='qldGenerationSecondary' type='button' onClick={() => setShowGenerateCardNumbersModal(false)}>Cancel</button>
                            <button className='qldGenerationPrimary' type='button' disabled={isCreatingQldCardNumbers} onClick={handleCreateQldCardNumbers}>
                                {isCreatingQldCardNumbers ? 'Generating...' : 'Generate Numbers'}
                            </button>
                        </div>
                    </div>
                </ModalComponent>

                <ModalComponent showModal={showGenerateCardsModal} setShowModal={setShowGenerateCardsModal} contentClassName='qldGenerationModal' contentStyle={{ maxWidth: '680px', padding: 0 }}>
                    <div className='qldGenerationModalInner'>
                        <div className='qldGenerationModalHeader'>
                            <span className='qldGenerationModalIcon'><svg aria-hidden='true' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/><path d='M14 2v6h6'/><path d='M8 13h8'/><path d='M8 17h5'/></svg></span>
                            <div><h2>Generate Student Card PDFs</h2><p>Review the eligible students and assigned card numbers for this class.</p></div>
                        </div>
                        <div className='qldGenerationModalBody'>
                        <div className='qldGenerationStats'><span><strong>{qldCardStudentsReadyForPdf.length}</strong> ready</span><span><strong>{qldCardStudentsMissingSignature.length}</strong> skipped</span></div>
                        {qldCardStudentsMissingSignature.length > 0 && (
                            <div className='qldGenerationWarning'>
                                {qldCardStudentsMissingSignature.length} completed student{qldCardStudentsMissingSignature.length === 1 ? '' : 's'} will be skipped because no declaration signature is saved yet.
                            </div>
                        )}
                        <div className='qldGenerationStudentList'>
                            {qldCardEligibleStudents.map((student, index) => (
                                <div key={student.id} className='qldGenerationStudentRow'>
                                    <span className='qldGenerationStudentName'>{toTitleCase(buildQldFullName(student))}<small>{!hasQldCardSignature(student) ? 'Missing declaration signature' : (student.cardNumber ? 'Existing number' : 'Number will be assigned')}</small></span>
                                    <span className={`qldGenerationCardNumber ${!hasQldCardSignature(student) ? 'isSkipped' : ''}`}>
                                        {!hasQldCardSignature(student)
                                            ? 'Skipped'
                                            : (student.cardNumber || (availableQldCardNumbers[qldCardStudentsMissingNumbers.findIndex((row) => row.id === student.id)]?.number || 'No number available'))}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {isLoadingQldCardNumbers && <div className='qldGenerationLoading'>Loading available card numbers...</div>}
                        {!isLoadingQldCardNumbers && availableQldCardNumbers.length < qldCardStudentsMissingNumbers.length && (
                            <div className='qldGenerationWarning'>
                                Not enough available QLD card numbers for this class. Add more numbers first.
                            </div>
                        )}
                        </div>
                        <div className='qldGenerationModalActions'>
                            <button className='qldGenerationSecondary' type='button' onClick={() => setShowGenerateCardsModal(false)}>Cancel</button>
                            <button
                                className='qldGenerationPrimary'
                                type='button'
                                disabled={isGeneratingQldCards || isLoadingQldCardNumbers || qldCardStudentsReadyForPdf.length === 0 || availableQldCardNumbers.length < qldCardStudentsMissingNumbers.length}
                                onClick={handleGenerateQldCards}
                            >
                                {isGeneratingQldCards ? 'Generating PDFs...' : 'Generate PDFs'}
                            </button>
                        </div>
                    </div>
                </ModalComponent>
            </div>
        </div>
    );
};

export default DisplayQldAdmin;
